import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import emailjs from '@emailjs/browser';
import Reveal from './Reveal';
import { contactHref, EMAIL, GITHUB_USER, LINKEDIN_URL } from '../content/portfolio';

const Contact: React.FC = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    message?: string;
  }>({});

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};
    if (!formData.name.trim()) newErrors.name = t('contact.nameRequired');
    else if (formData.name.trim().length < 2) newErrors.name = t('contact.nameMinLength');
    if (!formData.email.trim()) newErrors.email = t('contact.emailRequired');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = t('contact.emailInvalid');
    if (!formData.message.trim()) newErrors.message = t('contact.messageRequired');
    else if (formData.message.trim().length < 10)
      newErrors.message = t('contact.messageMinLength');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_default';
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_default';
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'your_public_key';

      try {
        if (
          serviceId !== 'service_default' &&
          templateId !== 'template_default' &&
          publicKey !== 'your_public_key'
        ) {
          await emailjs.send(
            serviceId,
            templateId,
            {
              from_name: formData.name,
              from_email: formData.email,
              message: formData.message,
              to_name: 'Isaac Ribeiro Leão',
              reply_to: formData.email,
            },
            publicKey
          );
        } else {
          throw new Error('EmailJS not configured');
        }
      } catch {
        const subject = encodeURIComponent(`Portfolio Contact: ${formData.name}`);
        const body = encodeURIComponent(
          `Name: ${formData.name}\nEmail: ${formData.email}\n\n${formData.message}`
        );
        window.open(
          `mailto:isaacribeiroleao@gmail.com?subject=${subject}&body=${body}`,
          '_blank'
        );
      }

      setSubmitStatus({ success: true, message: t('contact.successMessage') });
      setFormData({ name: '', email: '', message: '' });
    } catch {
      setSubmitStatus({ success: false, message: t('contact.errorMessage') });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  const field =
    'w-full rounded-none bg-code-bg/10 px-4 py-3 text-xl text-code-bg placeholder:text-code-bg/60 focus:bg-code-bg/20 focus:outline-none';
  const label = 'mb-1.5 block text-base';

  const links = [
    { label: t('contact.phone'), href: 'https://wa.me/5586998650525', value: '+55 (86) 99865-0525' },
    { label: t('contact.linkedin'), href: LINKEDIN_URL, value: 'linkedin.com/in/isaacleao' },
    { label: t('contact.github'), href: `https://github.com/${GITHUB_USER}`, value: `github.com/${GITHUB_USER}` },
  ];

  const bookingHref = contactHref(language);
  const hasBooking = bookingHref.startsWith('http');

  return (
    <section
      id="contact"
      data-ink="#0c0b09"
      data-field="#e6b450"
      className="flex min-h-[100svh] flex-col justify-between bg-accent-primary pb-16 pt-28 text-code-bg sm:pb-20"
    >
      <div className="container-responsive">
        <Reveal>
          <h2 className="max-w-3xl font-serif text-4xl font-medium sm:text-5xl">
            {language === 'pt' ? 'Vamos conversar sobre a sua vaga.' : "Let's talk about your open role."}
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="mt-5 text-xl">
            {language === 'pt' ? 'Escreva sobre a vaga, o time e a stack.' : 'Write about the role, the team and the stack.'}
          </p>
        </Reveal>
        <Reveal delay={0.16}>
          <a
            href={`mailto:${EMAIL}`}
            className="mt-12 block break-words font-serif text-[clamp(40px,10.6vw,190px)] leading-[0.95] tracking-[-0.03em] underline-offset-[0.12em] hover:underline"
          >
            isaacribeiroleao@
            <br />
            gmail.com
          </a>
        </Reveal>
        {hasBooking && (
          <a href={bookingHref} target="_blank" rel="noopener noreferrer" className="mt-8 inline-block text-xl underline underline-offset-4">
            {language === 'pt' ? 'Ou agende uma conversa' : 'Or book a conversation'}
          </a>
        )}
      </div>

      <div className="container-responsive mt-20 grid gap-14 lg:grid-cols-[1fr_1.2fr]">
        <ul className="space-y-5 self-start text-xl">
          {links.map((l) => (
            <li key={l.label}>
              <span className="block text-base">{l.label}</span>
              <a href={l.href} target="_blank" rel="noopener noreferrer" className="break-all underline underline-offset-4 hover:opacity-70">
                {l.value}
              </a>
            </li>
          ))}
        </ul>

        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          {(
            [
              ['name', t('contact.nameLabel')],
              ['email', t('contact.emailLabel')],
              ['message', t('contact.messageLabel')],
            ] as const
          ).map(([name, text]) => (
            <div key={name}>
              <label htmlFor={name} className={label}>
                {text}
              </label>
              {name === 'message' ? (
                <textarea id={name} name={name} value={formData[name]} onChange={handleChange} rows={4} className={`${field} resize-y`} aria-invalid={!!errors[name]} />
              ) : (
                <input id={name} name={name} type={name === 'email' ? 'email' : 'text'} value={formData[name]} onChange={handleChange} className={field} aria-invalid={!!errors[name]} />
              )}
              {errors[name] && <p className="mt-1 text-base font-medium text-[#6b1608]">{errors[name]}</p>}
            </div>
          ))}
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-code-bg px-7 py-4 text-xl font-medium text-accent-primary transition-transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60"
          >
            {isSubmitting ? t('contact.sending') : t('contact.submitButton')}
          </button>
          {submitStatus && (
            <p role="status" className={`text-xl font-medium ${submitStatus.success ? '' : 'text-[#6b1608]'}`}>
              {submitStatus.message}
            </p>
          )}
        </form>
      </div>
    </section>
  );
};

export default Contact;
